import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FiArrowLeft,
    FiSave,
    FiAlertCircle,
    FiCheckCircle,
    FiRefreshCw,
    FiMail,
    FiUser,
    FiActivity,
    FiHome,
    FiInfo
} from 'react-icons/fi';
import { getDoctorById, updateDoctor } from './doctorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDepartments, getSpecialtiesByDepartment } from '../specialties/taxonomyService';
import { sendPasswordResetEmail } from '../firebase';
import './EditDoctor.css';

export default function EditDoctor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        gender: '',
        dob: '',
        guildId: '',
        departmentId: '',
        specialtyId: '',
        departmentName: '',
        specialtyName: '',
        registrationNumber: '',
        registrationAuthority: '',
        experienceYears: 0,
        primaryHospital: '',
        primaryAddress: '',
        secondaryHospital: '',
        secondaryAddress: '',
        bio: '',
        membershipStartDate: '',
        membershipEndDate: '',
        education: [],
    });

    const [newEdu, setNewEdu] = useState('');

    const [guilds, setGuilds] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadData();
    }, [id]);

    async function loadData() {
        if (!id) return;
        try {
            setError('');
            setLoading(true);
            const [doctor, guildsData, deptsData] = await Promise.all([
                getDoctorById(id),
                getAllGuilds(),
                getAllDepartments()
            ]);

            if (doctor) {
                // Map Firestore data to form fields, handling potentially missing fields or old names
                setFormData({
                    fullName: doctor.fullName || doctor.name || '',
                    email: doctor.email || '',
                    phone: doctor.phone || '',
                    gender: doctor.gender || '',
                    dob: doctor.dob ? (doctor.dob.toDate ? doctor.dob.toDate().toISOString().split('T')[0] : doctor.dob) : '',
                    guildId: doctor.guildId || '',
                    departmentId: doctor.departmentId || '',
                    specialtyId: doctor.specialtyId || '',
                    departmentName: doctor.departmentName || '',
                    specialtyName: doctor.specialtyName || doctor.specialty || '',
                    registrationNumber: doctor.registrationNumber || doctor.medicalRegistrationNumber || '',
                    registrationAuthority: doctor.registrationAuthority || '',
                    experienceYears: doctor.experienceYears || doctor.yearsOfExperience || 0,
                    primaryHospital: doctor.primaryHospital || doctor.primaryHospitalName || '',
                    primaryAddress: doctor.primaryAddress || doctor.primaryWorkplaceAddress || '',
                    secondaryHospital: doctor.secondaryHospital || doctor.secondaryHospitalName || '',
                    secondaryAddress: doctor.secondaryAddress || doctor.secondaryWorkplaceAddress || '',
                    bio: doctor.bio || '',
                    membershipStartDate: doctor.membershipStartDate ? (doctor.membershipStartDate.toDate ? doctor.membershipStartDate.toDate().toISOString().split('T')[0] : doctor.membershipStartDate) : '',
                    membershipEndDate: doctor.membershipEndDate ? (doctor.membershipEndDate.toDate ? doctor.membershipEndDate.toDate().toISOString().split('T')[0] : doctor.membershipEndDate) : '',
                    education: doctor.education || [],
                });
            } else {
                setError('Doctor not found');
            }
            setGuilds(guildsData);
            setDepartments(deptsData);

            // If doctor has a department, load specialties for it
            if (doctor && doctor.departmentId) {
                const specs = await getSpecialtiesByDepartment(doctor.departmentId);
                setSpecialties(specs);
            }
        } catch (err) {
            console.error('Error loading doctor data:', err);
            setError('Failed to load doctor data');
        } finally {
            setLoading(false);
        }
    }

    async function handleDepartmentChange(e) {
        const deptId = e.target.value;
        const dept = departments.find(d => d.id === deptId);

        setFormData(prev => ({
            ...prev,
            departmentId: deptId,
            departmentName: dept ? dept.name : '',
            specialtyId: '',
            specialtyName: ''
        }));

        if (deptId) {
            try {
                const specData = await getSpecialtiesByDepartment(deptId);
                setSpecialties(specData);
            } catch (err) {
                console.error('Error loading specialties:', err);
            }
        } else {
            setSpecialties([]);
        }
    }

    function handleSpecialtyChange(e) {
        const specId = e.target.value;
        const spec = specialties.find(s => s.id === specId);

        setFormData(prev => ({
            ...prev,
            specialtyId: specId,
            specialtyName: spec ? spec.name : ''
        }));
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'experienceYears' ? parseInt(value) || 0 : value
        }));
        setError('');
        setSuccess('');
    }

    function addEducation() {
        if (newEdu.trim()) {
            setFormData(prev => ({
                ...prev,
                education: [...prev.education, newEdu.trim()]
            }));
            setNewEdu('');
        }
    }

    function removeEducation(index) {
        setFormData(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
    }

    async function handleSubmit(e) {
        e.preventDefault();

        // Validate membership dates
        if (formData.membershipStartDate && formData.membershipEndDate) {
            if (new Date(formData.membershipEndDate) < new Date(formData.membershipStartDate)) {
                setError('Membership expiry date cannot be before the start date');
                return;
            }
        }

        try {
            setSaving(true);
            setError('');
            setSuccess('');

            // Prepare updates - remove email as it shouldn't be changed here easily
            const { email, dob, membershipStartDate, membershipEndDate, education, ...updates } = formData;

            // Re-add dates as Date objects if provided
            if (dob) updates.dob = new Date(dob);
            if (membershipStartDate) updates.membershipStartDate = new Date(membershipStartDate);
            if (membershipEndDate) updates.membershipEndDate = new Date(membershipEndDate);
            updates.education = education;

            await updateDoctor(id, updates);
            setSuccess('Profile updated successfully!');

            setTimeout(() => {
                navigate('/dashboard/doctors');
            }, 1500);
        } catch (err) {
            console.error('Error updating doctor:', err);
            setError(err.message || 'Failed to update doctor profile');
        } finally {
            setSaving(false);
        }
    }

    async function handleSendInvite() {
        try {
            setInviting(true);
            setError('');
            setSuccess('');

            const { auth } = await import('../firebase');
            await sendPasswordResetEmail(auth, formData.email);

            setSuccess('An invitation (password reset) email has been sent successfully.');
        } catch (err) {
            console.error('Error sending invite:', err);
            setError('Failed to send invitation email. Please try again.');
        } finally {
            setInviting(false);
        }
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading doctor profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <button className="btn-icon" onClick={() => navigate('/dashboard/doctors')}>
                        <FiArrowLeft />
                    </button>
                    <h1>Edit Doctor Profile</h1>
                </div>
                <div className="header-actions">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleSendInvite}
                        disabled={inviting || saving}
                    >
                        {inviting ? <FiRefreshCw className="spin-icon" /> : <FiMail />}
                        <span>Send Invitation</span>
                    </button>
                </div>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-error">
                            <FiAlertCircle />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            <FiCheckCircle />
                            <span>{success}</span>
                        </div>
                    )}

                    {/* Basic Info Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiUser />
                            <h3>Basic Information</h3>
                        </div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email (Read-only)</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    disabled
                                    className="input-disabled"
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Gender</label>
                                <select name="gender" value={formData.gender} onChange={handleChange}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Date of Birth</label>
                                <input
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Guild *</label>
                                <select
                                    name="guildId"
                                    value={formData.guildId}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Guild</option>
                                    {guilds.map(guild => (
                                        <option key={guild.id} value={guild.id}>{guild.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Professional Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiActivity />
                            <h3>Professional Details</h3>
                        </div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Department *</label>
                                <select
                                    name="departmentId"
                                    value={formData.departmentId}
                                    onChange={handleDepartmentChange}
                                    required
                                >
                                    <option value="">Select Department</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Specialty *</label>
                                <select
                                    name="specialtyId"
                                    value={formData.specialtyId}
                                    onChange={handleSpecialtyChange}
                                    required
                                    disabled={!formData.departmentId}
                                >
                                    <option value="">Select Specialty</option>
                                    {specialties.map(spec => (
                                        <option key={spec.id} value={spec.id}>{spec.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Registration Number</label>
                                <input
                                    type="text"
                                    name="registrationNumber"
                                    value={formData.registrationNumber}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Registration Authority</label>
                                <input
                                    type="text"
                                    name="registrationAuthority"
                                    value={formData.registrationAuthority}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Years of Experience</label>
                                <input
                                    type="number"
                                    name="experienceYears"
                                    value={formData.experienceYears}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Education Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiActivity />
                            <h3>Education & Qualifications</h3>
                        </div>
                        <div className="form-group full-width">
                            <label>Add Qualification (e.g. MBBS, MD)</label>
                            <div className="input-with-button">
                                <input
                                    type="text"
                                    value={newEdu}
                                    onChange={(e) => setNewEdu(e.target.value)}
                                    placeholder="Enter qualification..."
                                />
                                <button
                                    type="button"
                                    className="btn-add"
                                    onClick={addEducation}
                                >
                                    Add
                                </button>
                            </div>
                            {formData.education && formData.education.length > 0 && (
                                <div className="tags-container">
                                    {formData.education.map((edu, index) => (
                                        <div key={index} className="tag">
                                            <span>{edu}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeEducation(index)}
                                                className="tag-remove"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Workplace Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiHome />
                            <h3>Workplace Information</h3>
                        </div>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Primary Hospital/Clinic</label>
                                <input
                                    type="text"
                                    name="primaryHospital"
                                    value={formData.primaryHospital}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Primary Address</label>
                                <textarea
                                    name="primaryAddress"
                                    value={formData.primaryAddress}
                                    onChange={handleChange}
                                    rows="2"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Secondary Hospital/Clinic (Optional)</label>
                                <input
                                    type="text"
                                    name="secondaryHospital"
                                    value={formData.secondaryHospital}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Secondary Address (Optional)</label>
                                <textarea
                                    name="secondaryAddress"
                                    value={formData.secondaryAddress}
                                    onChange={handleChange}
                                    rows="2"
                                />
                            </div>
                        </div>
                    </div>

                    {/* About Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiInfo />
                            <h3>Bio & About</h3>
                        </div>
                        <div className="form-group full-width">
                            <label>Short Bio</label>
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                rows="4"
                                maxLength="500"
                                placeholder="Tell us about the doctor..."
                            />
                        </div>
                    </div>

                    {/* Membership Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <FiActivity />
                            <h3>Membership Information</h3>
                        </div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Membership Start Date</label>
                                <input
                                    type="date"
                                    name="membershipStartDate"
                                    value={formData.membershipStartDate}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Membership Expiry Date</label>
                                <input
                                    type="date"
                                    name="membershipEndDate"
                                    value={formData.membershipEndDate}
                                    onChange={handleChange}
                                    min={formData.membershipStartDate}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => navigate('/dashboard/doctors')}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <FiRefreshCw className="spin-icon" />
                                    <span>Saving Changes...</span>
                                </>
                            ) : (
                                <>
                                    <FiSave />
                                    <span>Save Profile</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
